const EventEmitter = require('events');
const { mavlink20 } = require('./mavlink');
const jspack = require('jspack').jspack;
const fs = require('fs');

// Opcodes
const OP_None = 0;
const OP_TerminateSession = 1;
const OP_ResetSessions = 2;
const OP_ListDirectory = 3;
const OP_OpenFileRO = 4;
const OP_ReadFile = 5;
const OP_CreateFile = 6;
const OP_WriteFile = 7;
const OP_RemoveFile = 8;
const OP_CreateDirectory = 9;
const OP_RemoveDirectory = 10;
const OP_OpenFileWO = 11;
const OP_TruncateFile = 12;
const OP_Rename = 13;
const OP_CalcFileCRC32 = 14;
const OP_BurstReadFile = 15;
const OP_Ack = 128;
const OP_Nack = 129;

// Error codes
const ERR_None = 0;
const ERR_Fail = 1;
const ERR_FailErrno = 2;
const ERR_InvalidDataSize = 3;
const ERR_InvalidSession = 4;
const ERR_NoSessionsAvailable = 5;
const ERR_EndOfFile = 6;
const ERR_UnknownCommand = 7;
const ERR_FileExists = 8;
const ERR_FileProtected = 9;
const ERR_FileNotFound = 10;

const HDR_Len = 12;
const MAX_Payload = 239;

class FTP_OP {
    constructor(seq, session, opcode, size, reqOpcode, burstComplete, offset, payload) {
        this.seq = seq;
        this.session = session;
        this.opcode = opcode;
        this.size = size;
        this.reqOpcode = reqOpcode;
        this.burstComplete = burstComplete;
        this.offset = offset;
        this.payload = payload;
    }

    pack() {
        const hdr = jspack.Pack("<HBBBBBBI", [this.seq, this.session, this.opcode, this.size, this.reqOpcode, this.burstComplete, 0, this.offset]);
        return Buffer.concat([Buffer.from(hdr), this.payload]);
    }

    static unpack(message) {
        const hdr = Buffer.from(message.payload.slice(0, 12), 'binary');
        const [seq, session, opcode, size, reqOpcode, burstComplete, bc, offset] = jspack.Unpack("<HBBBBBBI", hdr);
        const payload = message.payload.slice(12, 12 + size);
        return new FTP_OP(seq, session, opcode, size, reqOpcode, burstComplete, offset, payload);
    }
}

class MavlinkFTP {
    constructor(targetSystem, targetComponent, sendMavlinkMsg, sendToMainWindow) {
        this.seq = 0;
        this.targetNetwork = 0;
        this.targetSystem = targetSystem;
        this.targetComponent = targetComponent;
        this.sendMavlinkMsg = sendMavlinkMsg;
        this.sendToMainWindow = sendToMainWindow;

        this.settings = {
            debug: 1,
            pktLossTx: 0,
            pktLossRx: 0,
            maxBacklog: 5,
            burstReadSize: 80,
            // Use the full MAVLink FTP payload (MAX_Payload=239) and a
            // deeper queue so upload throughput is not limited by RTT
            // the way MAVProxy's 5*80 defaults are. Effective ceiling
            // is roughly writeQsize*writeSize / RTT.
            writeSize: 239,
            writeQsize: 16,
            retryTime: 0.5,
            // Number of OP_ReadFile gap-fill requests kept in flight
            // simultaneously during the post-EOF cleanup phase.
            // Serial gap-fills (window=1) cost 1 RTT each; for ~700
            // gaps that adds many seconds on top of the burst. A
            // larger window pipelines them: total cleanup time
            // ~= ceil(gaps / window) * RTT. Bumped to 16 since the
            // ArduPilot FTP server tolerates it well and it shaved
            // another ~3s vs window=8 in benchmarks.
            gapFillWindow: 16
        };
        this.cmdGetPath = null;
        this.cmdRmPath = null;
        this.cmdRmdirPath = null;
        this.cmdRenamePath = null;
        this.cmdRenameNewPath = null;

        this.session = 0;
        this.opStart = null;
        this.lastOp = null;
        this.lastOpTime = Date.now();

        this.fh = null;

        this.listResult = []; // Initialize listResult to store the list results
        this.listPath = null; // Initialize listPath to store the list path
        this.totalSize = 0;
        this.dirOffset = 0;

        this.filename = null;
        this.readGaps = [];
        this.readGapTimes = {};
        this.openRetries = 0;
        this.readRetries = 0;
        this.readTotal = 0;
        this.duplicates = 0;
        this.lastRead = null;
        this.lastBurstRead = null; 
        this.rtt = 0.5;
        this.reachedEof = false;
        this.backlog = 0;
        this.burstSize = 80;

        this.writeList = null;
        this.writeBlockSize = 0;
        this.writeAcks = 0;
        this.writeTotal = 0;
        this.writeFileSize = 0;
        this.writeIdx = 0;
        this.writeRecvIdx = -1;
        this.writePending = 0;
        this.writeLastSend = null;
        this.warnedComponent = false;

    }

    sendFTPMessage(op) {
        op.seq = this.seq;
        const payload = op.pack();
        const plen = payload;
        if (plen < MAX_Payload + HDR_Len) {
            const padding = Buffer.alloc(HDR_Len + MAX_Payload - plen);
            payload = Buffer.concat([payload, padding]);
        };

        const msg = new mavlink20.messages.file_transfer_protocol(
            this.targetNetwork, // target_network
            this.targetSystem, // target_system
            this.targetComponent, // target_component
            payload.toString('binary')
        );
        this.sendMavlinkMsg(msg);
        this.seq = (this.seq + 1) % 256
        this.lastOp = op;
        this.lastOpTime = Date.now();
    }

    cmdList(ftpPath) {
        console.log('MAV FTP cmd list', ftpPath);
        this.listPath = ftpPath;
        this.dirOffset = 0;
        this.totalSize = 0;
        this.listResult = [];

        const encName = Buffer.from(ftpPath, 'ascii');
        const op = new FTP_OP(
            this.seq++, // seq
            this.session, // session
            OP_ListDirectory, // opcode
            encName.length, // size
            0, // reqOpcode
            0, // burstComplete
            0, // offset
            encName // payload
        );

        this.sendFTPMessage(op);
    }

    cmdGet(ftpPath, savePath) {
        console.log('MAV FTP cmd get', ftpPath, savePath);
        if (!ftpPath) {
            console.error('No file path provided');
            return;
        };
        if (!savePath) {
            console.error('No save path provided');
            return;
        };
        // Reset local read state without emitting a terminate event
        // (which would hide the renderer's progress modal as soon as we
        // start). Any prior open file handle is flushed/closed first.
        this._resetReadState({ silent: true });

        this.cmdGetPath = ftpPath;
        this.opStart = Date.now();
        //callback?
        this.readRetries = 0;
        this.duplicates = 0;
        this.reachedEof = false;
        // Use the full MAVLink payload as our burst chunk size. Smaller
        // values (e.g. 80) caused ArduPilot to send only ~one packet
        // per BurstReadFile and severely limited throughput.
        this.burstSize = MAX_Payload;
        const encName = Buffer.from(ftpPath, 'ascii');
        this.filename = savePath;
        this.openRetries = 0;
        const op = new FTP_OP(
            this.seq, // seq
            this.session, // session
            OP_OpenFileRO, // opcode
            encName.length, // size
            0, // reqOpcode
            0, // burstComplete
            0, // offset
            encName // payload
        );
        this.sendFTPMessage(op);
    }

    cmdRm(ftpPath) {
        console.log('MAV FTP cmd rm', ftpPath);
        this.cmdRmPath = ftpPath;
        const encName = Buffer.from(ftpPath, 'ascii');
        const op = new FTP_OP(
            this.seq, // seq
            this.session, // session
            OP_RemoveFile, // opcode
            encName.length, // size
            0, // reqOpcode
            0, // burstComplete
            0, // offset
            encName // payload
        );
        this.sendFTPMessage(op);
    };

    cmdRmdir(ftpPath) {
        console.log('MAV FTP cmd rmdir', ftpPath);
        this.cmdRmdirPath = ftpPath
        const encName = Buffer.from(ftpPath, 'ascii');
        const op = new FTP_OP(
            this.seq, // seq
            this.session, // session
            OP_RemoveDirectory, // opcode
            encName.length, // size
            0, // reqOpcode
            0, // burstComplete
            0, // offset
            encName // payload
        );
        this.sendFTPMessage(op);
    };

    cmdTerminateSession() {
        console.log('MAV FTP cmd terminate session');
        this.terminateSession();
    };

    cmdRename(ftpPath, newPath) {
        console.log('MAV FTP cmd rename', ftpPath, newPath);
        this.cmdRenamePath = ftpPath;
        this.cmdRenameNewPath = newPath;
        const encName = Buffer.from(ftpPath, 'ascii');
        const encNewName = Buffer.from(newPath, 'ascii');
        const payload = Buffer.concat([encName, Buffer.from([0]), encNewName]);
        const op = new FTP_OP(
            this.seq, // seq
            this.session, // session
            OP_Rename, // opcode
            payload.length, // size
            0, // reqOpcode
            0, // burstComplete
            0, // offset
            payload // payload
        );
        this.sendFTPMessage(op);
    };

    cmdMkdir(ftpPath) {
        console.log('MAV FTP cmd mkdir', ftpPath);
        const encName = Buffer.from(ftpPath, 'ascii');
        this.filename = ftpPath;
        const op = new FTP_OP(
            this.seq, // seq
            this.session, // session
            OP_CreateDirectory, // opcode
            encName.length, // size
            0, // reqOpcode
            0, // burstComplete
            0, // offset
            encName // payload
        );
        this.sendFTPMessage(op);
    };

    cmdPut(localPath, ftpPath) {
        console.log('MAV FTP cmd upload', localPath, '->', ftpPath);
        this.terminateSession();
        this.filename = localPath;
        if (this.writeList !== null) {
            console.error('FTP write in progress');
            return;
        }
        if (!this.fh) {
            try {
                this.fh = fs.openSync(this.filename, 'r'); 
            } catch (error) {
                console.error('Failed to open file:', error);
                return;
            }
        }
        const fileSize = fs.fstatSync(this.fh).size;
        this.writeBlockSize = this.settings.writeSize;
        this.writeFileSize = fileSize;
        const writeBlockCount = Math.ceil(fileSize / this.writeBlockSize);
        console.log('MAV FTP upload size', fileSize, 'bytes,', writeBlockCount, 'blocks @', this.writeBlockSize);

        // self.write_list = set(range(write_blockcount))
        this.writeList = new Set(Array.from({ length: writeBlockCount }, (_, i) => i));
        this.writeAcks = 0;
        this.writeTotal = writeBlockCount;
        this.writeIdx = 0;
        this.writeRecvIdx = -1;
        this.writePending = 0;
        this.writeLastSend = null;
        // Perf counters
        this.writeSends = 0;        // total OP_WriteFile packets put on the wire
        this.writeResends = 0;      // sends for blocks that had been sent before
        this.writeSentOnce = new Set();
        this._lastPutProgressEmit = 0;

        //callback?

        this.readRetries = 0;
        this.opStart = Date.now();
        const encName = Buffer.from(ftpPath, 'ascii');
        const op = new FTP_OP(this.seq, this.session, OP_CreateFile, encName.length, 0, 0, 0, encName);
        this.sendFTPMessage(op);
    }

    cmdCrc(ftpPath) {
        this.filename = ftpPath;
        this.opStart = Date.now();
        console.log('MAV FTP cmd crc', ftpPath);
        const encName = Buffer.from(ftpPath, 'ascii');
        const op = new FTP_OP(
            this.seq, // seq
            this.session, // session
            OP_CalcFileCRC32, // opcode
            encName.length, // size
            0, // reqOpcode
            0, // burstComplete
            0, // offset
            encName // payload
        );
        this.sendFTPMessage(op);
    }

    update(message) {
        if (message._name !== 'FILE_TRANSFER_PROTOCOL') {
            return;
        };
        const op = FTP_OP.unpack(message);
        this.lastOpTime = Date.now();

        switch (op.reqOpcode) {
            case OP_None:
                break;
            case OP_TerminateSession:
                // Reply to a TerminateSession we sent. Local cleanup
                // already happened before the message went out, so do
                // NOT call terminateSession() again here -- doing so
                // would flush a still-open download mid-transfer and
                // make the partial file look "completed".
                console.log('FTP TerminateSession reply received');
                break;
            case OP_ResetSessions:
                console.log('ResetSessions');
                break;
            case OP_ListDirectory:
                this.handleListReply(op);
                break;
            case OP_OpenFileRO:
                this.handleOpenROReply(op);
                break;
            case OP_Ack:
                this.handleAck(op);
                break;
            case OP_Nack:
                this.handleNack(op);
                break;
            case OP_BurstReadFile:
                this.handleBurstRead(op);
                break;
            case OP_CreateFile:
                this.handleCreateFileReply(op);
                break;
            case OP_WriteFile:
                this.handleWriteReply(op);
                break;
            case OP_RemoveFile:
            case OP_RemoveDirectory:
                this.handleRemoveReply(op);
                break;
            case OP_Rename:
                this.handleRenameReply(op);
                break;
            case OP_CreateDirectory:
                this.handleMkdirReply(op);
                break;
            case OP_ReadFile:
                this.handleReplyRead(op);
                break;
            case OP_CalcFileCRC32:
                this.handleCRCReply(op);
                break;
            default:
                console.log('Unhandled FTP reqOpcode:', op.reqOpcode);
        }
    }

    handleListReply(op) {
        if (op.opcode === OP_Ack) {
            const dirEntries = op.payload.toString().split('\x00').filter(Boolean);
            dirEntries.forEach(d => {
                if (d.length === 0) {
                    return;
                }
                ;
                this.dirOffset += 1;
                let name, type, size;
                if (d.startsWith('D')) {
                    name = d.slice(1);
                    type = 'D';
                    size = 0;
                } else if (d.startsWith('F')) {
                    const parts = d.slice(1).split('\t');
                    name = parts[0];
                    type = 'F';
                    size = parseInt(parts[1], 10);
                } else {
                    console.log(d);
                    return;
                }

                // Check if the file or directory is already in the list
                const alreadyExists = this.listResult.some(item => item.name === name && item.type === type);
                if (!alreadyExists) {
                    if (type === 'D') {
                        console.log("D", name)
                        this.listResult.push({name: name, type: type, size: size});
                    } else if (type === 'F') {
                        this.listResult.push({name: name, type: type, size: size});
                        this.totalSize += size;
                    }
                } else {
                    console.error(`Skipping duplicate entry: ${name} (${type})`);
                }
            });
            // console.log("handleListReply", this.listResult);
            // Ask for more
            const more = this.lastOp;
            more.offset = this.dirOffset;
            this.sendFTPMessage(more);
        } else if (op.opcode === OP_Nack && op.payload.length === 1 && op.payload.charCodeAt(0) === ERR_EndOfFile) {
            console.log(`Total size ${this.totalSize / 1024.0} kByte`);
            console.log("List result", this.listPath, this.listResult);
            if (this.listPath) {
                this.sendToMainWindow('mav-ftp-list-response', this.targetSystem, this.listPath, this.listResult);
            }
            this.totalSize = 0;
            this.listPath = null;
            this.listResult = [];
        } else {
            // Non-EOF NAK or unexpected opcode — abort the listing so
            // waiting callers (onceMavFtpListResponse) are not left hanging.
            console.log('LIST error:', op);
            if (this.listPath) {
                this.sendToMainWindow('mav-ftp-list-response', this.targetSystem, this.listPath, null);
            }
            this.totalSize = 0;
            this.listPath = null;
            this.listResult = [];
        }
    }

    handleAck(op) {
        console.log('FTP Ack:', op.payload.toString());
    }

    handleNack(op) {
        console.error('FTP Nack:', op.payload.toString());
    }

    _resetReadState({ silent = false } = {}) {
        this._stopStallWatchdog();
        this._stopWriteWatchdog();
        this.cmdGetPath = null;

        // Flush / close any open file handle so the browser_fs shim can
        // trigger a download for a completed GET.
        if (this.fh !== null && this.fh !== undefined) {
            try {
                if (typeof fs._flushHandle === 'function') fs._flushHandle(this.fh);
                if (typeof fs.closeSync === 'function') fs.closeSync(this.fh);
            } catch (err) {
                console.warn('Error closing FTP file handle:', err);
            }
        }
        this.fh = null;
        this.filename = null;
        this.writeList = null;
        this.readGaps = [];
        this.readTotal = 0;
        this.readGapTimes = {};
        this.lastRead = null;
        this.lastBurstRead = null;
        if (this._gapInFlight) this._gapInFlight.clear();
        this.session = (this.session + 1) % 256;
        this.reachedEof = false;
        this.backlog = 0;
        this.duplicates = 0;
        this.pendingPackets = new Map();
        this._lastResendOfs = -1;
        this._lastResendTime = 0;
        // MAVProxy-style "never pause the burst" state.
        // expectedOffset: next byte the burst is expected to deliver.
        // Advanced on every received packet to (op.offset + payload.length).
        // Used by burstComplete to ask for continuation, and by EOF
        // nack to compute trailing gap.
        this.expectedOffset = 0;
        // Index of which gap we're currently filling.
        this._gapFillIdx = 0;
        // Stall watchdog state.
        // _stallLastOffset: expectedOffset observed at last watchdog
        //   tick; used to suppress stall when burst is still making
        //   progress (slow link != stalled link).
        // _stallSameCount: number of consecutive stall resends at the
        //   same expectedOffset without any progress in between. Used
        //   to back off and eventually reopen the session.
        this._stallLastOffset = -1;
        this._stallSameCount = 0;
        // Map<offset, sendTime> of OP_ReadFile gap-fill requests
        // currently in flight (post-EOF pipelining).
        this._gapInFlight = new Map();
        if (!silent) {
            this.sendToMainWindow('mav-ftp-terminate-session', this.targetSystem);
        }
    }

    terminateSession(op) {
        // Avoid emitting duplicate "terminated" logs when called repeatedly
        // (e.g. once from cmdPut on entry, again from sendMoreWrites on
        // completion). Only log when there was actually live state to clear.
        const wasActive = this.fh !== null || this.writeList !== null || this.cmdGetPath;
        this._resetReadState({ silent: false });
        if (wasActive) console.log('FTP session terminated');
    }

    // Fire-and-forget OP_TerminateSession so the server flushes/closes the
    // file. We don't wait for the ack -- local cleanup follows immediately.
    _sendServerTerminateSession() {
        if (this.session === undefined || this.session === null) return;
        const op = new FTP_OP(
            this.seq,
            this.session,
            OP_TerminateSession,
            0,
            0,
            0,
            0,
            Buffer.alloc(0)
        );
        this.sendFTPMessage(op);
    }

    _sendBurstRead(offset) {
        const op = new FTP_OP(
            this.seq,
            this.session,
            OP_BurstReadFile,
            this.burstSize,
            0,
            0,
            offset,
            Buffer.alloc(0)
        );
        this.lastBurstRead = Date.now();
        this.lastBurstSend = Date.now();
        this.sendFTPMessage(op);
    }

    _sendSingleRead(offset, length) {
        // One-shot OP_ReadFile to fill a known gap (single packet).
        // Reply is delivered via handleReplyRead.
        const op = new FTP_OP(
            this.seq,
            this.session,
            OP_ReadFile,
            length,
            0,
            0,
            offset,
            Buffer.alloc(0)
        );
        this.lastBurstRead = Date.now();
        this.lastBurstSend = Date.now();
        this.sendFTPMessage(op);
    }

    _reopenForResume() {
        if (!this.cmdGetPath) return;
        // Bump session so server sees a new request.
        this.session = (this.session + 1) % 256;
        const encName = Buffer.from(this.cmdGetPath, 'ascii');
        const op = new FTP_OP(
            this.seq,
            this.session,
            OP_OpenFileRO,
            encName.length,
            0,
            0,
            0,
            encName
        );
        this.lastBurstRead = Date.now();
        this.lastBurstSend = Date.now();
        this.sendFTPMessage(op);
    }

    _startStallWatchdog() {
        this._stopStallWatchdog();
        // Reset progress tracker so the first tick treats the current
        // expectedOffset as the baseline.
        this._stallLastOffset = this.expectedOffset;
        this._stallSameCount = 0;
        this._stallTimer = setInterval(() => {
            if (this.fh === null || this.fh === undefined || !this.cmdGetPath) {
                this._stopStallWatchdog();
                return;
            }
            const now = Date.now();
            if (this.reachedEof) {
                // We're filling residual gaps via OP_ReadFile.
                // Re-issue the current gap-fill if no reply for a
                // while (server may have lost the request).
                if (this.readGaps.length === 0) {
                    this._stopStallWatchdog();
                    return;
                }
                const sinceRecv = this.lastBurstRead ? (now - this.lastBurstRead) : 0;
                const sinceSend = this.lastBurstSend ? (now - this.lastBurstSend) : Infinity;
                if (sinceRecv > 1500 && sinceSend > 1500) {
                    this.stallResends = (this.stallResends || 0) + 1;
                    this._sendNextGapFill();
                }
                return;
            }
            // Progress-aware stall detection.
            //
            // The previous logic only looked at elapsed time since the
            // last received packet. On a slow serial link (or when
            // ArduPilot is busy with other MAVLink traffic) the gap
            // between burst packets can legitimately exceed several
            // seconds even though the transfer is still making
            // progress -- those events were being flagged as stalls
            // and the resend would race with in-flight packets,
            // generating duplicates.
            //
            // Now: if expectedOffset advanced since the last tick the
            // burst is healthy -- reset the timers. Only when we see
            // *no* progress AND no recent send do we consider it a
            // real stall.
            if (this.expectedOffset !== this._stallLastOffset) {
                this._stallLastOffset = this.expectedOffset;
                this._stallSameCount = 0;
                // Treat progress as an implicit "recv" so timers
                // restart from now.
                this.lastBurstRead = now;
                return;
            }
            const sinceRecv = this.lastBurstRead ? (now - this.lastBurstRead) : 0;
            const sinceSend = this.lastBurstSend ? (now - this.lastBurstSend) : Infinity;
            // Exponential backoff between consecutive stall resends
            // that didn't produce any progress: 500ms, 1s, 2s, 4s
            // (capped). The first step matches MAVProxy's retry_time
            // -- low enough that a lost burstComplete packet doesn't
            // burn seconds of wall time, while progress-aware
            // detection (above) keeps healthy bursts from tripping
            // it.
            const backoffSteps = [500, 1000, 2000, 4000];
            const idx = Math.min(this._stallSameCount, backoffSteps.length - 1);
            const recvThreshold = backoffSteps[idx];
            // Avoid issuing two bursts closer than recvThreshold apart.
            const sendThreshold = recvThreshold;
            if (sinceRecv < recvThreshold || sinceSend < sendThreshold) return;

            // After several consecutive no-progress stalls, the
            // server-side session may be wedged. Reopen the file
            // (bumps session) to get a fresh stream instead of
            // resending into the void.
            if (this._stallSameCount >= 5) {
                if (this.settings.debug > 0) {
                    console.log('FTP: stall persisted at', this.expectedOffset, '- reopening session');
                }
                this._stallSameCount = 0;
                this._reopenForResume();
                return;
            }

            if (this.settings.debug > 0) {
                console.log('FTP: stall detected, resending BurstReadFile at', this.expectedOffset,
                    '(attempt', this._stallSameCount + 1, ', waited', sinceRecv, 'ms)');
            }
            this.stallResends = (this.stallResends || 0) + 1;
            this._stallSameCount += 1;
            this._sendBurstRead(this.expectedOffset);
        }, 200);
    }

    _stopStallWatchdog() {
        if (this._stallTimer) {
            clearInterval(this._stallTimer);
            this._stallTimer = null;
        }
    }

    // Watchdog for the upload (put) path. sendMoreWrites is normally driven
    // by handleWriteReply, so if the last few OP_WriteFile packets get lost
    // there is nothing to kick the state machine and the transfer stalls.
    // This timer periodically forces a sendMoreWrites; the timer-based
    // pending-decrement inside sendMoreWrites then re-sends an un-acked
    // block.
    _startWriteWatchdog() {
        this._stopWriteWatchdog();
        const rttSec = (typeof this.rtt === 'number' && this.rtt > 0) ? this.rtt : 0.5;
        const periodMs = Math.max(200, Math.round(rttSec * 1000));
        this._writeStuckCount = 0;
        this._writeTimer = setInterval(() => {
            // Done? Stop ourselves.
            if (this.fh === null || this.writeList === null || this.writeList.size === 0) {
                this._stopWriteWatchdog();
                return;
            }
            const now = Date.now();
            const idle = this.writeLastSend ? (now - this.writeLastSend) : Infinity;
            // Only intervene if the pipeline has gone quiet (no recent send
            // means no recent ack either, since each ack triggers a send).
            if (idle < periodMs) return;
            this._writeStuckCount += 1;
            if (this.settings.debug > 0) {
                console.log(`FTP upload watchdog tick #${this._writeStuckCount}: writeList=${this.writeList.size} pending=${this.writePending} idle=${idle}ms`);
            }
            // After many consecutive stuck ticks give up to avoid an
            // infinite spin if the link is truly dead.
            if (this._writeStuckCount > 20) {
                console.error('FTP upload watchdog: giving up after repeated stalls');
                this._stopWriteWatchdog();
                this.terminateSession();
                return;
            }
            // Force a re-send opportunity by clearing writePending so the
            // loop in sendMoreWrites can repopulate the window with the
            // outstanding (un-acked) blocks.
            this.writePending = 0;
            this.sendMoreWrites();
        }, periodMs);
    }

    _stopWriteWatchdog() {
        if (this._writeTimer) {
            clearInterval(this._writeTimer);
            this._writeTimer = null;
        }
    }

    handleOpenROReply(op) {
        console.log('handleOpenROReply called', op);
        if (op.opcode === OP_Ack) {
            if (this.filename === null) {
                return;
            }
            // If we already have an open file handle (resume after
            // ERR_InvalidSession), keep it and continue from the
            // current file size. Otherwise open a fresh handle.
            let resumeOffset = 0;
            if (this.fh !== null && this.fh !== undefined) {
                try { resumeOffset = fs.fstatSync(this.fh).size; } catch (_) {}
                console.log('FTP: resuming download at offset', resumeOffset);
            } else {
                try {
                    this.fh = fs.openSync(this.filename, 'w+');
                } catch (error) {
                    console.error('Failed to open file:', error);
                    this.terminateSession();
                    return;
                }
            }
            this._sendBurstRead(resumeOffset);
            this._startStallWatchdog();
        } else {
            console.error('FTP open failed');
            this.terminateSession();
        }
    }

    writePayload(op) {
        const buffer = Buffer.from(op.payload, 'binary');
        try {
            const written = fs.writeSync(this.fh, buffer, 0, buffer.length, op.offset);
            this.readTotal += written;
            this._emitProgressThrottled();
        } catch (err) {
            console.error('Failed to write to file:', err);
            this.terminateSession();
        }
    }

    _emitProgressThrottled() {
        // Progress events drive a React re-render in the renderer
        // (LinearProgress + textual size). Firing one per packet
        // (~4500 events for a 1MB log) was the dominant cost during
        // bulk FTP downloads. Throttle to ~10Hz; checkReadFinished
        // emits a final corrective event with the exact size.
        const now = Date.now();
        if (!this._lastProgressEmit || (now - this._lastProgressEmit) >= 100) {
            this._lastProgressEmit = now;
            const dt = this.opStart ? (now - this.opStart) / 1000.0 : 0;
            const rate = dt > 0 ? (this.readTotal / dt) / 1024.0 : 0;
            this.sendToMainWindow('mav-ftp-get-progress', this.targetSystem, this.cmdGetPath, this.readTotal, rate);
        }
    }

    checkReadFinished() {
        if (this.reachedEof && this.readGaps.length === 0) {
            console.log('FTP read finished');
            const ofs = fs.fstatSync(this.fh).size;
            const dt = (Date.now() - this.opStart) / 1000.0;
            const rate = (ofs / dt) / 1024.0;
            // callback?
            console.log(`Wrote ${ofs} bytes to ${this.filename} in ${dt} s ${rate} kByte/s`);
            console.log(`FTP stats: duplicates=${this.duplicates} gapFills=${this.gapFills || 0} stallResends=${this.stallResends || 0}`);
            // Push a final progress event reflecting the actually-written
            // size so the UI shows the real total before the session ends.
            if (this.cmdGetPath) {
                this.sendToMainWindow('mav-ftp-get-progress', this.targetSystem, this.cmdGetPath, ofs, rate);
            }
            this.terminateSession();
            return true;
        }
        return false;
    }

    sendGapRead(g) {
        const offset = g[0]; 
        const length = g[1];
        if (this.settings.debug > 0) {
            console.log(`Gap read of ${length} at ${offset} rem=${this.readGaps.length} blog=${this.backlog}`);
        }
        const read = new FTP_OP(
            this.seq, // seq
            this.session, // session
            OP_ReadFile, // opcode
            length, // size
            0, // reqOpcode
            0, // burstComplete
            offset, // offset
            Buffer.alloc(0) // payload
        );
        this.sendFTPMessage(read);
        this.readGaps[offset] = this.lastGapSend;
        this.backlog += 1;
    }

    checkReadSend() {
        if (this.readGaps.length === 0) {
            return;
        }
        const g = this.readGaps[0];
        const now = Date.now();
        const dt = now - this.readGapTimes[g];
        if (!this.reachedEof) {
            Object.keys(this.readGapTimes).forEach(g => {
                if (this.readGapTimes[g] == 0) {
                    this.sendGapRead(g);
                }
            })
            return;
        }
        if (this.readGapTimes[g] > 0 && dt > this.settings.retryTime) {
            if (this.backlog > 0) {
                this.backlog -= 1;
            }
            this.readGapTimes[g] = 0;
        }
        if (this.readGapTimes[g] != 0) {
            return;
        }
        if (!this.reachedEof && this.backlog >= this.settings.maxBacklog) {
            return;
        }
        if (now - this.lastGapSend < 0.05) {
            return;
        }
        this.sendGapRead(g);
    }

    handleBurstRead(op) {
        if (this.fh === null || this.filename === null) {
            console.error("FTP Unexpected burst read");
            return;
        }
        if (op.session !== this.session ) {
            console.error('Old FTP session');
            return;
        }
        this.lastBurstRead = Date.now();
        // MAVProxy-style "never pause the burst" model:
        //   * Write each packet directly at op.offset (browser_fs
        //     polyfill zero-fills holes via Uint8Array allocation).
        //   * Track `expectedOffset` = next byte the burst is expected
        //     to deliver; advance it on every packet (== high-water
        //     mark of received offsets).
        //   * On op.offset > expectedOffset, record a gap; do NOT
        //     interrupt the burst -- the server keeps streaming.
        //   * On op.offset < expectedOffset, it's a duplicate from
        //     overlap (watchdog resends, etc).
        //   * On burstComplete=1, ask for next burst from
        //     expectedOffset. The server treats that as continuation.
        //   * Gaps are filled via OP_ReadFile only AFTER EOF nack.
        if (op.opcode === OP_Ack && this.fh) {
            try {
                const len = op.payload.length;
                if (op.offset < this.expectedOffset) {
                    // Below high-water mark: may either fill a gap
                    // we recorded earlier (out-of-order arrival) or
                    // be a true duplicate from burst overlap.
                    if (this._consumeGap(op.offset, len)) {
                        this.writePayload(op);
                    } else {
                        this.duplicates += 1;
                    }
                } else {
                    if (op.offset > this.expectedOffset) {
                        // Record gap for later fill (or for matching
                        // by any straggler packet from the prior
                        // burst).
                        this._addReadGap(this.expectedOffset, op.offset - this.expectedOffset);
                    }
                    this.writePayload(op);
                    this.expectedOffset = op.offset + len;
                }
                if (op.burstComplete) {
                    // MAVProxy semantics: continue the burst from the
                    // high-water mark (op.offset + len). Gaps are
                    // filled via single-packet OP_ReadFile *after*
                    // EOF -- never by re-bursting.
                    //
                    // The previous implementation re-burst from the
                    // lowest recorded gap whenever any gap existed.
                    // On a link with even a small loss rate that
                    // caused the server to replay the entire current
                    // burst window (up to ~80 packets) on every loss,
                    // and the duplicate bytes dominated the channel
                    // (~100% duplicates measured = ~2x slower than
                    // MAVProxy on the same hardware).
                    const continueFrom = op.offset + len;
                    const now = Date.now();
                    if (this._lastResendOfs !== continueFrom || (now - (this._lastResendTime || 0)) > 50) {
                        this._lastResendOfs = continueFrom;
                        this._lastResendTime = now;
                        this._sendBurstRead(continueFrom);
                    }
                }
            } catch (error) {
                console.error('Failed to write to file:', error);
                this.terminateSession();
                return;
            }
        } else if (op.opcode === OP_Nack) {
            const eCode = op.payload.charCodeAt(0);
            if (this.settings.debug > 0) {
                console.log("FTP: burst nack eCode=", eCode, op);
            }
            if (eCode === ERR_EndOfFile || eCode === 0) {
                // Server says EOF at op.offset. If our expectedOffset
                // is behind that, there's a trailing hole we never
                // received -- record it as a gap.
                if (!this.reachedEof) {
                    if (op.offset > this.expectedOffset) {
                        this._addReadGap(this.expectedOffset, op.offset - this.expectedOffset);
                        this.expectedOffset = op.offset;
                    }
                    if (this.settings.debug > 0) {
                        console.log("EOF at", this.expectedOffset, "with", this.readGaps.length, "gaps t=", (Date.now() - this.opStart) / 1000.0);
                    }
                    this.reachedEof = true;
                }
                if (this.readGaps.length > 0) {
                    this._sendNextGapFill();
                } else {
                    this.checkReadFinished();
                }
            } else if (eCode === ERR_InvalidSession || eCode === ERR_NoSessionsAvailable) {
                // Server lost / invalidated the file session. Re-open
                // the file to get a fresh session and resume.
                console.log('FTP: invalid session, reopening file at', this.cmdGetPath);
                this._reopenForResume();
            }
        } else {
            console.error('FTP: Unexpected burst read:', op);
        }
    }

    _addReadGap(offset, length) {
        // Split a hole into burst-sized chunks so each chunk fits in
        // a single OP_ReadFile reply.
        let off = offset;
        let remaining = length;
        while (remaining > MAX_Payload) {
            this.readGaps.push([off, MAX_Payload]);
            off += MAX_Payload;
            remaining -= MAX_Payload;
        }
        if (remaining > 0) this.readGaps.push([off, remaining]);
    }

    _consumeGap(offset, length) {
        // If `[offset, offset+length)` overlaps any recorded gap,
        // shrink/split that gap to remove the overlap and return
        // true. Returns false if no gap matched (pure duplicate).
        const pEnd = offset + length;
        for (let i = 0; i < this.readGaps.length; i++) {
            const gOff = this.readGaps[i][0];
            const gLen = this.readGaps[i][1];
            const gEnd = gOff + gLen;
            if (pEnd <= gOff || offset >= gEnd) continue;
            // overlap region [lo, hi)
            const lo = offset > gOff ? offset : gOff;
            const hi = pEnd < gEnd ? pEnd : gEnd;
            // Replace gap with the (possibly empty) left + right
            // remnants of the original gap minus the overlap.
            this.readGaps.splice(i, 1);
            if (hi < gEnd) this.readGaps.splice(i, 0, [hi, gEnd - hi]);
            if (lo > gOff) this.readGaps.splice(i, 0, [gOff, lo - gOff]);
            return true;
        }
        return false;
    }

    _sendNextGapFill() {
        // Pump gap-fill requests, keeping up to `gapFillWindow` in
        // flight at once. Each gap that isn't currently in flight
        // and hasn't been requested gets a fresh OP_ReadFile.
        const window = Math.max(1, this.settings.gapFillWindow || 1);
        const now = Date.now();
        // Reap stale in-flight entries: if a request has been
        // outstanding for more than 1500ms we assume reply was
        // lost and re-issue it (drops it from the map so the loop
        // below picks it up again).
        for (const [off, sentAt] of this._gapInFlight) {
            if (now - sentAt > 1500) this._gapInFlight.delete(off);
        }
        for (let i = 0; i < this.readGaps.length && this._gapInFlight.size < window; i++) {
            const g = this.readGaps[i];
            if (this._gapInFlight.has(g[0])) continue;
            this._gapInFlight.set(g[0], now);
            this.gapFills = (this.gapFills || 0) + 1;
            this._sendSingleRead(g[0], g[1]);
        }
    }

    handleCreateFileReply(op) {
        console.log('handleCreateFileReply called', op);
        if (this.fh === null) {
            this.terminateSession();
            return;
        };
        if (op.opcode === OP_Ack) {
            this._startWriteWatchdog();
            this.sendMoreWrites();
        } else {
            this.terminateSession();
        };
    }

    putFinished(fileLength) {
        const dt = this.opStart ? (Date.now() - this.opStart) / 1000.0 : 0;
        const rate = dt > 0 ? (fileLength / dt) / 1024.0 : 0;
        console.log(`Uploaded ${fileLength} bytes to ${this.filename} in ${dt.toFixed(2)} s ${rate.toFixed(2)} kByte/s`);
        console.log(`FTP upload stats: blocks=${this.writeTotal} sends=${this.writeSends} resends=${this.writeResends} acks=${this.writeAcks}`);
        this.sendToMainWindow('mav-ftp-put-response', this.targetSystem, this.filename, fileLength, true);
    }

    sendMoreWrites() {
        if (this.writeList.size === 0) {
            // Tell the server to close/flush the file before tearing down
            // local state. Without this, ArduPilot keeps the file handle
            // open and the upload appears truncated on the vehicle side.
            this._sendServerTerminateSession();
            this.putFinished(this.writeFileSize);
            this.terminateSession();
            return;
        }

        const now = Date.now();
        // Drop one pending slot if we've gone too long without any ack,
        // so the loop below will re-send an un-acked block.
        if (this.writeLastSend) {
            const rttSec = (typeof this.rtt === 'number' && this.rtt > 0) ? this.rtt : 0.5;
            const ageSec = (now - this.writeLastSend) / 1000;
            if (ageSec > Math.max(Math.min(10 * rttSec, 1), 0.2)) {
                this.writePending = Math.max(0, this.writePending - 1);
            }
        }

        const n = Math.min(this.settings.writeQsize - this.writePending, this.writeList.size);
        for (let i = 0; i < n; i++) {
            let idx = this.writeIdx;
            while (!this.writeList.has(idx)) {
                idx = (idx + 1) % this.writeTotal;
            }
            const ofs = idx * this.writeBlockSize;
            const buffer = Buffer.alloc(this.writeBlockSize);
            const bytesRead = fs.readSync(this.fh, buffer, 0, this.writeBlockSize, ofs);
            const data = buffer.slice(0, bytesRead);
            const write = new FTP_OP(
                this.seq, // seq
                this.session, // session
                OP_WriteFile, // opcode
                data.length, // size
                0, // reqOpcode
                0, // burstComplete
                ofs, // offset
                data // payload
            );
            this.sendFTPMessage(write);
            if (this.writeSentOnce && this.writeSentOnce.has(idx)) {
                this.writeResends += 1;
            } else if (this.writeSentOnce) {
                this.writeSentOnce.add(idx);
            }
            this.writeSends += 1;
            this.writeIdx = (idx + 1) % this.writeTotal;
            this.writePending += 1;
            this.writeLastSend = now;
        }
    }

    handleWriteReply(op) {
        if (this.settings.debug > 1) {
            console.log('handleWriteReply called', op);
        }
        if (this.fh === null) {
            // Session already torn down: silently drop straggling acks instead
            // of re-terminating (and re-logging) for every late packet.
            return;
        };
        if (op.opcode !== OP_Ack) {
            console.error('FTP write failed');
            this.terminateSession();
            return;
        };
        const idx = Math.ceil(op.offset / this.writeBlockSize);
        const count = (idx - this.writeRecvIdx) % this.writeTotal;

        this.writePending = Math.max(0, this.writePending - count);
        this.writeRecvIdx = idx;
        this.writeList.delete(idx);
        this.writeAcks += 1;
        this._writeStuckCount = 0;

        // Throttle progress events to ~10Hz (one per ack was the dominant
        // React re-render cost during large uploads, mirroring the download
        // path).
        const now = Date.now();
        if (!this._lastPutProgressEmit || (now - this._lastPutProgressEmit) >= 100) {
            this._lastPutProgressEmit = now;
            const dt = (now - this.opStart) / 1000.0;
            const rate = dt > 0 ? (this.writeAcks * this.writeBlockSize / dt) / 1024.0 : 0;
            this.sendToMainWindow('mav-ftp-put-progress', this.targetSystem, this.filename, this.writeAcks, this.writeTotal, rate, this.writeFileSize);
        }
        //callback?
        this.sendMoreWrites();

    }

    handleRemoveReply(op) {
        if (this.settings.debug > 0) {
            console.log('handleRemoveReply called', op);
        }
        if (op.reqOpcode === OP_RemoveFile) {
            this.sendToMainWindow('mav-ftp-rm-response', this.targetSystem, this.cmdRmPath, op.opcode === OP_Ack);
            this.cmdRmPath = null;
        } else if (op.reqOpcode === OP_RemoveDirectory) {
            this.sendToMainWindow('mav-ftp-rmdir-response', this.targetSystem, this.cmdRmdirPath, op.opcode === OP_Ack);
            this.cmdRmdirPath = null;
        }
    }

    handleRenameReply(op) {
        if (this.settings.debug > 0) {
            console.log('handleRenameReply called', op);
        }
        this.sendToMainWindow('mav-ftp-rename-response', this.targetSystem, this.cmdRenamePath, this.cmdRenameNewPath, op.opcode === OP_Ack);
    }

    handleMkdirReply(op) {
        if (this.settings.debug > 0) {
            console.log('handleMkdirReply called', op);
        }
        this.sendToMainWindow('mav-ftp-mkdir-response', this.targetSystem, this.filename, op.opcode === OP_Ack);
    }

    handleReplyRead(op) {
        if (this.settings.debug > 1) {
            console.log('handleReplyRead called', op);
        }
        if (this.fh === null || this.filename === null) {
            return;
        }
        if (op.opcode === OP_Ack && this.fh) {
            // Reply to an OP_ReadFile we issued for a known gap.
            // Locate the matching gap entry and write the bytes at
            // the requested offset (browser_fs places them
            // wherever).
            const len = op.payload.length;
            this._gapInFlight.delete(op.offset);
            const gIdx = this.readGaps.findIndex(g => g[0] === op.offset);
            if (gIdx >= 0) {
                this.writePayload(op);
                const gap = this.readGaps[gIdx];
                if (len >= gap[1]) {
                    this.readGaps.splice(gIdx, 1);
                } else {
                    // Partial fill: shrink the gap.
                    this.readGaps[gIdx] = [gap[0] + len, gap[1] - len];
                }
            } else {
                this.duplicates += 1;
            }
            if (this.readGaps.length > 0) {
                this._sendNextGapFill();
            } else if (this.reachedEof && this._gapInFlight.size === 0) {
                this.checkReadFinished();
            }
        } else if (op.opcode === OP_Nack) {
            const eCode = op.payload.charCodeAt(0);
            if (eCode === ERR_EndOfFile || eCode === 0) {
                // The gap we asked for is past true EOF -- drop it.
                this._gapInFlight.delete(op.offset);
                const gIdx = this.readGaps.findIndex(g => g[0] === op.offset);
                if (gIdx >= 0) this.readGaps.splice(gIdx, 1);
                this.reachedEof = true;
                if (this.readGaps.length > 0) {
                    this._sendNextGapFill();
                } else if (this._gapInFlight.size === 0) {
                    this.checkReadFinished();
                }
            } else {
                console.error("FTP: gap-fill ReadFile failed eCode=", eCode, op);
                this.terminateSession();
            }
        }
    }

    handleCRCReply(op) {
        if (this.settings.debug > 0) {
            console.log('handleCRCReply called', op);
        }
        if (op.opcode === OP_Ack && op.size === 4) {
            const crc = jspack.Unpack("<I", op.payload)[0];
            const now = Date.now();
            console.log(this.filename, 'CRC32:', crc);
        } else {
            console.error('CRC failed:', op);
        }
    }

    handleTerminateSession = () => {
        console.log('handleTerminateSession called');
        this.terminateSession();
    }
}

module.exports = MavlinkFTP;